// El encabezado de la home en el TELÉFONO para la interfaz nueva (grupo C) —
// ADR-2026-10-08-la-interfaz-nueva-llega-al-telefono. Ocupa el lugar del carrusel de modos
// (`MobileModeHero`) y del hero del Agente: el saludo con la pregunta de escritorio y las
// tres cards por destino, que abren el sheet ya en su sección.

import type { ReactNode } from 'react';
import { SaludoDeLaHome } from './SaludoDeLaHome';
import { useShellMobile } from './shellMobile';
import { ListaDeHerramientas } from './SheetPorDestino';
import { CardAcademyHome } from '../../academia/CardAcademyHome';
import { HomeTuProducto } from './HomeTuProducto';
import type { MockProductV2 } from '../mockData';

/**
 * El color de arriba: los tintes de las tres secciones (lavanda de Redes, crema de
 * Catálogos, celeste de Anuncios) como manchas suaves, más saturadas que las cards para que
 * se lean sobre ellas. Sin esto la primera pantalla era gris y texto negro, y el color
 * aparecía recién en las cards.
 */
const FONDO =
  'radial-gradient(85% 50% at 0% 10%, #D6BDF0 0%, rgba(214,189,240,0) 72%),' +
  'radial-gradient(75% 45% at 100% 2%, #F0D9A0 0%, rgba(240,217,160,0) 72%),' +
  'radial-gradient(120% 55% at 65% 48%, rgb(var(--brand-primary-rgb) / 0.27) 0%, rgb(var(--brand-primary-rgb) / 0) 74%)';

export function HomeMobilePorDestino({
  nombre,
  idCompany = null,
  idMarca = null,
  productoReciente = null,
  aviso = null,
}: {
  nombre?: string | null;
  /** Para "Tu producto al centro" del gratis que recién entra. */
  idCompany?: string | null;
  idMarca?: string | null;
  productoReciente?: MockProductV2 | null;
  /** Un aviso que no puede esperar (el de cobro rechazado), justo debajo del saludo. */
  aviso?: ReactNode;
}) {
  // El gratis que recién entra: sin Academia y con una bajada que lo invita a elegir
  // (ADR-2026-10-23-la-home-del-telefono-para-el-gratis-que-recien-entra).
  const { gratisQueRecienEntra, cuentaCargando } = useShellMobile();
  return (
    // `-mx-5 px-5`: el fondo mide la pantalla entera sin mover el contenido (el contenedor
    // de la home tiene sangría de 20px). SIN `overflow-x-clip`: en Safari del iPhone
    // recortaba también hacia arriba, y el fondo se cortaba en el saludo en vez de subir
    // detrás de los créditos (ADR-2026-10-18-el-fondo-de-la-home-del-telefono-no-se-recorta).
    // Nada se sale por los costados: el fondo va `inset-x-0` y el carrusel scrollea solo.
    // El gratis que recién entra: la sección mide lo que queda de pantalla para que sus tres
    // accesos lo repartan (ADR-2026-10-23-los-accesos-del-gratis-nuevo-llenan-la-pantalla). Se
    // resta lo de arriba (lo mismo que sube el fondo: safe area, 5.25rem y el aviso de generando)
    // y la barra de abajo (4rem + safe area) con 0.75rem de aire. El `-mb-24` anula el `pb-24`
    // del contenedor, que se sumaba al padding del `main` y dejaba ~120px de scroll vacío.
    <section
      className={`relative isolate -mx-5 flex flex-col gap-4 px-5 pb-2 pt-1 sm:hidden ${
        gratisQueRecienEntra
          ? // `pb-4`: si el contenido no entra y la sección crece, la última card queda arriba de la barra.
            '-mb-24 !pb-4 min-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-var(--aviso-generando-mobile,0px)-10rem)]'
          : ''
      }`}
    >
      {/* Sube hasta el borde físico de arriba —por detrás de los créditos, que flotan— y se
          funde con el gris de la página antes de la segunda card. El `top` anula todo lo
          que hay arriba de la sección: el `mainTopPadding` de `DashboardLayout` (safe area +
          3.5rem + 0.75rem + el aviso de "generando", si está) y el `pt-4` del contenedor
          de la home. Sin el `pt-4` quedaba una franja gris arriba. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 -z-10 h-[476px]"
        style={{
          top: 'calc(0px - env(safe-area-inset-top, 0px) - 5.25rem - var(--aviso-generando-mobile, 0px))',
          background: FONDO,
          WebkitMaskImage: 'linear-gradient(to bottom, #000 60%, transparent 100%)',
          maskImage: 'linear-gradient(to bottom, #000 60%, transparent 100%)',
        }}
      />
      {/* Sólo el saludo, sin bajada, para todas las cuentas
          (ADR-2026-10-27-la-home-del-telefono-sin-bajada). El gratis que recién entra tiene
          debajo su producto al centro, con su propio título
          (ADR-2026-10-23-la-home-del-gratis-nuevo-arranca-por-su-producto). */}
      <SaludoDeLaHome nombre={nombre} tamano="telefono" pregunta={null} />
      {/* El aviso de cobro rechazado va acá y no al pie, debajo de Academia, donde podía no
          verse (ADR-2026-11-03-el-aviso-de-cobro-debajo-del-saludo-en-el-telefono). */}
      {aviso}
      {/* Hasta saber qué cuenta es, sólo el saludo: ver `cuentaCargando`. Después, los mismos
          accesos por rubro para todas las cuentas, paguen o no
          (ADR-2026-10-27-los-accesos-por-rubro-para-todas-las-cuentas). */}
      {cuentaCargando ? null : (
        <>
          <HomeTuProducto idCompany={idCompany} idMarca={idMarca} producto={productoReciente} />
          {/* Academia Atlas debajo, discreta, menos para el gratis que recién entra
              (ADR-2026-10-23-la-home-del-telefono-para-el-gratis-que-recien-entra). 32 px de los
              accesos: el `gap-4` de la sección + `mt-4` (su default es `mt-12`). */}
          {!gratisQueRecienEntra && <CardAcademyHome className="mt-4" discreta />}
        </>
      )}
    </section>
  );
}

/**
 * "Herramientas" en la home del teléfono (grupo C): la MISMA lista que la pestaña Herramientas
 * y que la home de escritorio, en el lugar de "Ediciones rápidas" (debajo de Creaciones
 * recientes). Antes quedaba el bloque viejo, con siete ediciones y otro dibujo.
 */
export function HerramientasMobile() {
  const { herramientas, gratisQueRecienEntra } = useShellMobile();
  // El gratis que recién entra no las ve acá: cierran la pestaña Herramientas
  // (ADR-2026-10-23-la-home-del-telefono-para-el-gratis-que-recien-entra).
  if (!herramientas || gratisQueRecienEntra) return null;
  return (
    <section className="mt-8">
      <h2 className="mb-3 text-[22px] font-bold tracking-[-0.01em]">Herramientas</h2>
      <div className="-mx-3">
        <ListaDeHerramientas
          ediciones={herramientas.ediciones}
          modos={herramientas.modos}
          onElegir={(onAbrir) => onAbrir()}
        />
      </div>
    </section>
  );
}
